import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  Component,
} from "react";

import { useSelector, useDispatch } from "react-redux";

import {
  getRobotsList,
  createRobotTier,
  updateRobotTier,
  getGroupsList,
} from "./adminSlice";

import { fileSize } from "../../libs/utils";

import {
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Popover,
  Select,
  Table,
  Tag,
  Tooltip,
} from "antd";

import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  PlusSquareOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import { isNil } from "lodash";

const { Option } = Select;

const BYTE_IN_GB = 1073741824; // byte in GB for bandwidth conversion

function SelectTier(props) {
  const { record } = props;
  return (
    <Select
      defaultValue={record.tier && record.tier.tier}
      onChange={props.onChange}
    >
      <Option value="lite">lite (1GB)</Option>
      <Option value="basic">basic (10GB)</Option>
      <Option value="advanced">advanced (100GB)</Option>
    </Select>
  );
}

function SetCapacity(props) {
  const { record } = props;

  const capacity =
    record.tier && record.tier.capacity ? record.tier.capacity / BYTE_IN_GB : 0;

  return (
    <InputNumber
      defaultValue={capacity}
      max={100}
      min={1}
      onChange={props.onChange}
    />
  );
}

function SelectGroups(props) {
  const { record, groups } = props;

  const defaultValues = record.groups
    ? record.groups.map((item) => ({
        ...item,
        label: item.name,
        value: item.id,
        key: item.id,
      }))
    : [];

  const groupsOptions = groups.map((item) => ({
    ...item,
    label: item.name,
    value: item.id,
    key: item.id,
  }));

  return (
    <Select
      mode="multiple"
      placeholder="Please select user(s) to add to the group"
      defaultValue={defaultValues}
      onChange={props.onChange}
      style={{ width: "100%" }}
      labelInValue
    >
      {groupsOptions.map((item) => (
        <Option key={item.id} value={item.id}>
          {item.name}
        </Option>
      ))}
    </Select>
  );
}

function getInputNode(dataIndex, record, options) {
  switch (dataIndex) {
    case "group":
      return <SelectGroups record={record} groups={options.groups} />;

    case "tier.tier":
      return <SelectTier record={record} />;

    case "tier.capacity":
      return <SetCapacity record={record} />;

    default:
      return <Input />;
  }
}

function getTierColor(role) {
  switch (role) {
    case "advanced":
      return "#108ee9";
    case "basic":
      return "#2db7f5";
    default:
      return "#87d068";
  }
}

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  const inputNode = getInputNode(dataIndex, record, options);
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex.includes(".") ? dataIndex.split(".") : dataIndex}
          style={{
            margin: 0,
          }}
          rules={
            [
              // {
              //   // required: true,
              //   message: `Please Input ${title}!`,
              // },
            ]
          }
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const EditableTable = () => {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [showingKey, setShowingKey] = useState("");

  const dispatch = useDispatch();
  const robots = useSelector((state) => state.admin.robots);
  const groups = useSelector((state) => state.admin.groups);

  useEffect(() => {
    dispatch(getRobotsList());
    dispatch(getGroupsList());
  }, [dispatch]);

  const isEditing = (record) => record.key === editingKey;
  const isShowingAPIKey = (record) => record.key === showingKey;

  if (!robots && !groups) {
    return <></>;
  }
  const robotLists = robots && [
    ...robots.map((item, index) => ({ ...item, key: index })),
  ];

  const edit = (record) => {
    form.setFieldsValue({
      tier: {
        tier: "",
        used: "",
        capacity: "",
      },
      ...record,
    });

    setEditingKey(record.key);
  };

  const showAPIKey = (record) => {
    setShowingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const add = (record) => {
    dispatch(createRobotTier(record.id));
  };

  const save = async (key, id) => {
    try {
      const row = await form.validateFields();

      const { tier } = row;

      dispatch(
        updateRobotTier(id, {
          ...(!isNil(tier.tier) && { tier: tier.tier }),
          ...(!isNil(tier.used) && { used: tier.used }),
          ...(!isNil(tier.capacity) && {
            // converting back to byte value
            capacity:
              tier.capacity > 100 ? tier.capacity : tier.capacity * BYTE_IN_GB,
          }),
        })
      );

      setEditingKey("");
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      // editable: true,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      // editable: true,
    },
    {
      title: "Group",
      dataIndex: "group",
      key: "groups",
      // editable: true,
      render: (text, record, index) => {
        return (
          <>
            {record.groups &&
              record.groups.map((item) => <Tag>{item.name}</Tag>)}
          </>
        );
      },
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      // editable: true,
    },
    {
      title: "Tier",
      dataIndex: "tier.tier",
      key: "tier.tier",
      render: (text, record, index) => {
        return (
          <>
            {record.tier && (
              <Tag color={getTierColor(record.tier.tier)}>
                {record.tier.tier}
              </Tag>
            )}
          </>
        );
      },
      editable: true,
      filters:
        robotLists &&
        robotLists.length > 0 &&
        robotLists
          .map((item) => {
            return item.tier && item.tier.tier;
          })
          .filter((value, index, self) => self.indexOf(value) === index)
          .map((item) => {
            return { text: item, value: item };
          }),
      onFilter: (value, record) =>
        record.tier && record.tier && record.tier.tier.indexOf(value) === 0,
    },

    // {
    //   title: "Subscription Start Date",
    //   dataIndex: "tier.start_at",
    //   key: "tier.start_at",
    //   render: (_, record) => {
    //     return <>{record.tier && <span>{record.tier.start_at}</span>}</>;
    //   },
    //   // editable: true,
    // },

    // {
    //   title: "Subscription End Date",
    //   dataIndex: "tier.end_at",
    //   key: "tier.end_at",
    //   render: (_, record) => {
    //     return <>{record.tier && <span>{record.tier.end_at}</span>}</>;
    //   },
    //   // editable: true,
    // },

    {
      title: (
        <span style={{ cursor: "pointer" }}>
          Download Limit{" "}
          <Popover
            content={
              <div>
                <p>Total Allowed Download bandwidth for current month</p>
                <p>You can manually set download limit as well.</p>
                <p>
                  When it is renewed, it will be renewed to the corresponding
                  default value for the tier
                  <ul>
                    <li>Lite: 1GB</li>
                    <li>Basic: 10GB</li>
                    <li>Advanced: 100GB</li>
                  </ul>
                </p>
              </div>
            }
            title="Capacity"
          >
            <InfoCircleOutlined />
          </Popover>
        </span>
      ),
      dataIndex: "tier.capacity",
      key: "tier.capacity",
      render: (_, record) => {
        return (
          <>{record.tier && <span>{fileSize(record.tier.capacity)}</span>}</>
        );
      },
      editable: true,
    },

    {
      title: "Used",
      dataIndex: "tier.used",
      key: "tier.used",
      render: (_, record) => {
        return <>{record.tier && <span>{fileSize(record.tier.used)}</span>}</>;
      },
      editable: true,
    },

    {
      title: (
        <span style={{ cursor: "pointer" }}>
          API Key{" "}
          <Popover
            content={
              <div>
                <p>
                  API Key is hidden. To reveal it click on <KeyOutlined />. It
                  is only available for basic & advanced
                </p>
                <p>
                  This can be used in{" "}
                  <a
                    href="https://api.novarctech.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    API doc
                  </a>
                  <ul>
                    <li>
                      <i>X-APP-ID:</i> <b>SWR-####</b>
                    </li>
                    <li>
                      <i>X-API-KEY:</i>{" "}
                      <b>********-****-****-****-**********</b>
                    </li>
                  </ul>
                </p>
              </div>
            }
            title="API Key"
          >
            <InfoCircleOutlined />
          </Popover>
        </span>
      ),
      dataIndex: "api_key.secret_key",
      key: "api_key.secret_key",
      render: (_, record) => {
        const showingAPI = isShowingAPIKey(record);

        return showingAPI ? (
          <>
            {record.api_key && record.api_key.secret_key && (
              <span>{record.api_key.secret_key}</span>
            )}
          </>
        ) : (
          <></>
        );
      },
    },

    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Popconfirm
              title="Sure to Save?"
              onConfirm={() => save(record.key, record.id)}
            >
              <Button icon={<CheckOutlined />} />
            </Popconfirm>

            <Button icon={<CloseOutlined />} onClick={cancel} />
          </span>
        ) : (
          <>
            {!record.tier ? (
              <Button
                icon={<PlusSquareOutlined />}
                disabled={editingKey !== ""}
                // disabled={true}
                onClick={() => add(record)}
              />
            ) : (
              <>
                <Button
                  icon={<EditOutlined />}
                  disabled={editingKey !== ""}
                  // disabled={true}
                  onClick={() => edit(record)}
                />
                {record.tier && record.tier.tier !== "lite" && (
                  <Tooltip title={"Click to reveal API for basic & advanced"}>
                    <Button
                      icon={<KeyOutlined />}
                      disabled={editingKey !== ""}
                      // disabled={true}
                      onClick={() => showAPIKey(record)}
                    />
                  </Tooltip>
                )}{" "}
              </>
            )}
          </>
        );
      },
    },
  ];
  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        options: { groups },
      }),
    };
  });
  return (
    <Form form={form} component={false}>
      <Table
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        bordered
        dataSource={robotLists}
        columns={mergedColumns}
        rowClassName="editable-row"
        pagination={{
          onChange: cancel,
        }}
      />
    </Form>
  );
};

export default EditableTable;
